import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Play } from 'lucide-react';
import { Quiz } from '@/types/quiz';

interface QuizCardProps {
  quiz: Quiz;
  index: number;
  onTakeQuiz: (quiz: Quiz) => void;
}

export const QuizCard = ({ quiz, index, onTakeQuiz }: QuizCardProps) => {
  const getDifficultyColor = (difficulty: number) => {
    const colors = {
      1: 'bg-green-100 text-green-800',
      2: 'bg-blue-100 text-blue-800', 
      3: 'bg-yellow-100 text-yellow-800',
      4: 'bg-orange-100 text-orange-800',
      5: 'bg-red-100 text-red-800'
    };
    return colors[difficulty as keyof typeof colors] || colors[1];
  };

  const getDifficultyLabel = (difficulty: number) => {
    const labels = {
      1: '⭐ Beginner',
      2: '⭐⭐ Easy', 
      3: '⭐⭐⭐ Medium',
      4: '⭐⭐⭐⭐ Hard',
      5: '⭐⭐⭐⭐⭐ Expert'
    };
    return labels[difficulty as keyof typeof labels] || labels[1];
  };

  return (
    <Card className="card-interactive hover-lift group relative gradient-border bg-gradient-to-br from-card via-card to-accent/5 shadow-lg hover:shadow-3xl animate-fade-in max-w-sm mx-auto">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
      
      <CardContent className="p-4 relative z-10">
        <div className="flex flex-col space-y-4">
          {/* Header with title and badge in upper right */}
          <div className="relative">
            <h3 className="text-lg font-bold text-black pr-16">{quiz.title}</h3>
            <Badge className={`absolute top-0 right-0 ${getDifficultyColor(quiz.difficulty)} shadow-sm font-medium text-xs`}>
              {getDifficultyLabel(quiz.difficulty)}
            </Badge>
          </div>

          {quiz.description && (
            <p className="text-muted-foreground text-xs leading-relaxed bg-muted/30 p-2 rounded-lg border-l-2 border-primary/20 text-center">
              {quiz.description}
            </p>
          )}

          <div className="text-center">
            <p className="text-sm font-medium text-primary/80 mb-3">Test your knowledge!</p>
          </div>

          <div className="flex justify-center">
            <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 px-3 py-2 rounded-xl border-2 border-yellow-400/30 shadow-lg transform hover:scale-105 transition-all duration-200">
              <Trophy className="h-4 w-4 text-yellow-600" />
              <span className="font-bold text-yellow-800 text-sm">{quiz.points_reward} pts</span>
            </div>
          </div>

          <div className="text-center">
            <Button 
              onClick={() => onTakeQuiz(quiz)}
              className="btn-primary bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 px-6 py-2 rounded-xl font-semibold text-sm w-full"
            >
              <Play className="h-4 w-4 mr-2" />
              Take Quiz
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};